import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FiPaperclip, FiX } from 'react-icons/fi';
import { useAppDispatch } from '../../../app/store/store';
import { createTicket } from '../../../entities/ticket/model';
import { helpdeskSettingsApi } from '../../../api/services/helpdesk-settings.service';
import {
  TicketCreatePayload,
  TicketPriority,
  TicketTypeRef,
  TICKET_PRIORITY_LABELS,
} from '../../../types/ticketInterfaces';

interface TicketCreateModalProps {
  onClose: () => void;
  onCreated: () => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export const TicketCreateModal: React.FC<TicketCreateModalProps> = ({
  onClose,
  onCreated,
}) => {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState('');
  // Справочник типов — напрямую из API, без redux: модалка живёт недолго,
  // а список типов маленький. Пустой список = селект не показываем.
  const [ticketTypes, setTicketTypes] = useState<TicketTypeRef[]>([]);

  useEffect(() => {
    helpdeskSettingsApi
      .getTicketTypes(true)
      .then((res) => setTicketTypes(res.data))
      .catch(() => setTicketTypes([]));
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<TicketCreatePayload>({
    defaultValues: {
      subject: '',
      description: '',
      priority: 'medium',
      ticket_type: null,
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const maxSize = 25 * 1024 * 1024;
    const oversized = files.find((f) => f.size > maxSize);
    if (oversized) {
      setUploadError(`Файл "${oversized.name}" превышает 25 МБ`);
      return;
    }
    setUploadError('');
    setSelectedFiles((prev) => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: TicketCreatePayload) => {
    try {
      const payload: TicketCreatePayload = {
        ...data,
        // select отдаёт строку — приводим к number, пустое значение → null.
        ticket_type: data.ticket_type ? Number(data.ticket_type) : null,
      };
      await dispatch(
        createTicket({
          data: payload,
          files: selectedFiles.length > 0 ? selectedFiles : undefined,
        }),
      ).unwrap();
      onCreated();
    } catch {
      // ошибка уже в Redux
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black bg-opacity-40"
        onClick={onClose}
      />

      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Новая заявка</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Тема <span className="text-red-500">*</span>
            </label>
            <input
              {...register('subject', {
                required: 'Укажите тему заявки',
                minLength: { value: 3, message: 'Минимум 3 символа' },
              })}
              type="text"
              placeholder="Опишите кратко проблему"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400"
            />
            {errors.subject && (
              <p className="mt-1 text-xs text-red-500">{errors.subject.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Описание <span className="text-red-500">*</span>
            </label>
            <textarea
              {...register('description', {
                required: 'Укажите описание',
                minLength: { value: 10, message: 'Минимум 10 символов' },
              })}
              rows={5}
              placeholder="Подробно опишите вашу проблему или вопрос..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
            />
            {errors.description && (
              <p className="mt-1 text-xs text-red-500">{errors.description.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Приоритет
            </label>
            <select
              {...register('priority')}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white"
            >
              {(Object.entries(TICKET_PRIORITY_LABELS) as [TicketPriority, string][]).map(
                ([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ),
              )}
            </select>
          </div>

          {ticketTypes.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Тип заявки
              </label>
              <select
                {...register('ticket_type')}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white"
              >
                <option value="">Не выбран</option>
                {ticketTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Прикрепление файлов */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Вложения
            </label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 border border-dashed border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors w-full justify-center"
            >
              <FiPaperclip className="w-4 h-4" />
              Прикрепить файлы
            </button>

            {uploadError && (
              <p className="mt-1 text-xs text-red-500">{uploadError}</p>
            )}

            {selectedFiles.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {selectedFiles.map((file, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FiPaperclip className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span className="text-xs text-gray-700 truncate">{file.name}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="text-gray-400 hover:text-red-500 flex-shrink-0 ml-2"
                    >
                      <FiX className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Создание...' : 'Создать заявку'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
